/* @flow */

import { getEvaluatedDialup, getEffectiveType } from './utils/browserutil';
import {
  isOptedOut,
  isFromEU,
  getOptOutDetailCode
} from './utils/privacyutils';
import { apiVersion } from './utils';
import JSONP from './jsonp';
import { isFetchSupported, fetchJSON } from './utils/fetch';
import adrotation from './adrotation';
import bm from './bidmanager-facade';
import adserver from './adserver';
import adloader from './adloader';
import storage from './utils/storage';
import cookieManager from './utils/cookiemanager';
import adconfig from './adconfig';
import { logError, logInfo, logDebug, setLoggerStart } from './utils/logger';
import { isString, extend, param } from './utils/coreutils';
import idm from './idproviders/idpmanager-facade';
import globalParams from './globalparams';

/**
 * @description Implements ad configuration functionality
 * @exports adconfigUpdater
 * @requires  module:utils
 * @requires module:logger
 */

class _adconfigUpdater {
  _name: string;

  _win: WindowProxy;

  _doc: Document;

  _expiryTimeoutSec: number;

  _loadTimeout: number;

  _url: string;

  _pageInfo: pageInfo;

  _lastConfigLoadTime: number;

  _siteInfo: siteInfo;

  _bDeferredInited: boolean;

  constructor() {
    this._name = 'adconfigUpdater';
    this._win = window;
    this._doc = document;
    this._expiryTimeoutSec = 20 * 60;
    this._loadTimeout = 3000;
    this._url = '';
    this._lastConfigLoadTime = 0;
    this._bDeferredInited = false;
  }

  /**
   * session logging
   *
   * @returns {undefined}
   */

  _sessionLog(g: globalParam) {
    const optedout = isOptedOut() ? 1 : 0;
    // prettier-ignore
    const uh = isString(g.customVars._uh5) ? g.customVars._uh5 : '';
    const dApp = g.isDialup ? '1' : '0';
    const dEval = getEvaluatedDialup(g.isDialup) ? '1' : '0';
    const eType = getEffectiveType();
    const eu = isFromEU() ? 1 : 0;
    const optdetail = getOptOutDetailCode();

    const params: { [string]: string | number } = {
      uh,
      site: g.phInfo.site,
      optedout,
      dapp: dApp,
      dnw: eType,
      deval: dEval,
      eu
    };
    if (optdetail) {
      params.optdetail = optdetail;
    }

    const log = `sessLog:${param(params)}`;

    // Record logger start time
    setLoggerStart();
    // Log session log line
    logInfo(log);
  }

  /**
   * Deferred initialization of adserver and idm
   * Called after config is loaded to check disableAdsForEU flag
   * @returns {undefined}
   * @access private
   */
  _deferredInit() {
    // Early exit if already initialized
    if (this._bDeferredInited) {
      return;
    }
    // Early exit if ads disabled for EU (don't set flag, allows retry if flag changes)
    if (adconfig.isAdsDisabledForEU()) {
      return;
    }
    // Get global params and initialize
    const g = globalParams.get();
    adserver.init(this._win, this._doc, g);
    idm.init(this._win, this._doc, g);
    // Only set flag when actually initialized
    this._bDeferredInited = true;
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._loadTimeout = g.adConfig.loadTimeout;
    this._url = g.adConfig.url;
    this._siteInfo = g.phInfo;
    // initialize all the other components
    adconfig.init(w, d, g);
    adloader.init(w, d, g);
    // adserver and idm initialization deferred until after config is loaded
    // to check disableAdsForEU flag
    adrotation.init(w, d, g);
    cookieManager.init(w, d, g);
    // send session log
    this._sessionLog(g);
  }

  /**
   * _processConfigData - take cares of updating caches and propagating config change
   *
   * @external adConfig
   * @param {adConfig} data - AdConfig data
   * @returns {undefined}
   * @access private
   */
  _processConfigData(data: adConfig) {
    adconfig.processConfigData(data);
    const config: adConfig = adconfig.get();
    // update in-memory cache in ad config
    this._expiryTimeoutSec = config.settings.configExpirySec;

    // Deferred initialization of adserver and idm (before updateConfig calls)
    this._deferredInit();

    // propagate config update to other classes
    adrotation.updateConfig(config.settings.adRotate);
    // Skip bm, idm, adserver config updates if not initialized (disabled for EU)
    if (this._bDeferredInited) {
      bm.updateConfig(config.settings.headerBidding);
      idm.updateConfig(config.settings.userIds);
      adserver.updateConfig(config.settings.adServer);
    }
    adloader.updateConfig(config.settings.adloading);
    cookieManager.updateConfig(config.settings.cookieManager);

    // update local storage cache
    storage.storeConfig(data, this._siteInfo.site);
  }

  /**
   * _fetchConfig - checks for fetch support and uses fetch instead of jsonp
   *
   * @param {string} url - URL for config
   * @external jsonpConfig
   * @param {jsonpConfig} params - Extra JSONP params
   * @returns {Promise} - Fetch/JSONP promise
   * @access private
   */
  _fetchConfig(url: string, params: jsonpConfig): Promise<adConfig> {
    if (!isFetchSupported()) {
      return JSONP(url, params);
    }

    const fetchParams = extend(true, {}, params);
    fetchParams.data = fetchParams.data || {};
    fetchParams.data.format = 'json';
    return fetchJSON(url, fetchParams);
  }

  /**
   * _load - load config from server
   *
   * @param {boolean} bFirstLoad - Firstload?
   * @returns {Promise} - Config loading promise
   * @access private
   */
  _load(bFirstLoad: boolean): Promise<boolean> {
    const params: jsonpConfig = {
      data: {
        site: this._siteInfo.site,
        apiVersion: apiVersion(),
        brand: this._siteInfo.brand
      },
      timeout: this._loadTimeout,
      logPrefix: 'adconfig'
    };
    logDebug(`${this._name} Trying to fetch adconfig`);
    return this._fetchConfig(this._url, params)
      .then(
        (data: adConfig) => {
          if (data && data.settings) {
            this._processConfigData(data);
            this._lastConfigLoadTime = Date.now();
          }
          return data;
        },
        (error: string) => {
          logError(
            `Failed to get ad config from ${this._url} for ${
              this._siteInfo.site
            } firstload:${bFirstLoad ? 1 : 0}: ${error || ''}`
          );
        }
      )
      .then(() => true);
  }

  /**
   * loads adconfig from server. If available in cache, will return cache till expiry.
   *
   * @returns {Promise<*>} - Promise
   */
  load(bForce: boolean = false): Promise<boolean> {
    const bFirstLoad = this._lastConfigLoadTime === 0;
    let expiredCache: boolean = true;
    // get from localstorage for first time
    if (bFirstLoad) {
      const { expired, data, updatedTime } = storage.getConfig(
        this._siteInfo.site
      );
      if (!expired && 'settings' in data) {
        // $FlowFixMe[incompatible-indexer] storage cache validated via settings check
        this._processConfigData(data);
        if (Date.now() - updatedTime < this._expiryTimeoutSec * 1000) {
          this._lastConfigLoadTime = updatedTime;
          logDebug(`${this._name} Loaded config from storage for first run!`);
          expiredCache = false;
        }
      } else {
        expiredCache = expired;
      }
    }

    const curTime = Date.now();
    if (curTime - this._lastConfigLoadTime < this._expiryTimeoutSec * 1000) {
      logDebug(`${this._name} Last config is good to use!`);
      expiredCache = false;
    }
    return expiredCache || bForce
      ? this._load(bFirstLoad)
      : Promise.resolve(true);
  }
}

// export singleton object
const acu: _adconfigUpdater = new _adconfigUpdater();
export default acu;
